import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { subscriptionService } from '../services/subscriptionService';
import { useInfluencer } from '../contexts/InfluencerContext';
import type { SubscriptionPlan, SubscriptionState, PlanDetails } from '../types/subscription';

export interface UseSubscriptionReturn extends SubscriptionState {
  isLoading: boolean;
  availablePlans: PlanDetails[];
  isPlansLoading: boolean;
  checkFeatureAccess: (feature: 'replay' | 'tickReplay' | 'multiChart' | 'advancedAnalysis' | 'api' | 'aiAnalysis') => boolean;
  refreshSubscription: () => Promise<void>;
  openUpgradeModal: () => void;
  closeUpgradeModal: () => void;
  isUpgradeModalOpen: boolean;
  requiredPlanForFeature: (feature: string) => SubscriptionPlan;
  isAutoSubscribing: boolean;
}

export function useSubscription(): UseSubscriptionReturn {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { discountCode } = useInfluencer();
  const [state, setState] = useState<SubscriptionState>({
    plan: 'free',
    isActive: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [availablePlans, setAvailablePlans] = useState<PlanDetails[]>([]);
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isAutoSubscribing, setIsAutoSubscribing] = useState(false);
  const autoSubscribeAttempted = useRef(false);

  const fetchSubscription = useCallback(async () => {
    if (!isLoaded) return;

    setIsLoading(true);

    try {
      const subscriptionState = await subscriptionService.getSubscriptionFromClerk(user);
      setState(subscriptionState);
    } catch (error) {
      console.error('[useSubscription] Error fetching subscription:', error);
      setState(subscriptionService.getDefaultState());
    } finally {
      setIsLoading(false);
    }
  }, [user, isLoaded]);

  const fetchPlans = useCallback(async () => {
    setIsPlansLoading(true);
    try {
      const plans = await subscriptionService.fetchPlansFromClerk();
      setAvailablePlans(plans);
    } catch (error) {
      console.error('[useSubscription] Error fetching plans:', error);
      // Show user-friendly error - don't fail silently
      setAvailablePlans([]);
    } finally {
      setIsPlansLoading(false);
    }
  }, []);

  // Auto-subscribe user with 100% discount coupon
  // Creates a real Stripe subscription with the coupon applied
  const autoSubscribe = useCallback(async () => {
    // Prevent duplicate calls - check and set flag IMMEDIATELY to prevent race conditions
    if (autoSubscribeAttempted.current) {
      console.log('[useSubscription] Skipping auto-subscribe - already attempted');
      return false;
    }
    // Set flag immediately before any async operations to prevent parallel calls
    autoSubscribeAttempted.current = true;

    if (!discountCode || !isLoaded || !user) {
      autoSubscribeAttempted.current = false; // Reset if preconditions not met
      return false;
    }

    // Check if user already has an active subscription (not free)
    // Check both React state AND Clerk metadata to avoid stale state issues
    if (state.plan !== 'free' && state.isActive) {
      console.log('[useSubscription] Skipping auto-subscribe - already has active subscription (state)');
      return false;
    }

    // Also check Clerk metadata directly to catch cases where state is stale
    const metadata = user.publicMetadata || {};
    const subscriptionStatus = metadata.subscriptionStatus as string;
    const subscriptionPlan = metadata.subscriptionPlan as string;
    if (subscriptionStatus === 'active' || (subscriptionPlan && subscriptionPlan !== 'free')) {
      console.log('[useSubscription] Skipping auto-subscribe - already has subscription (metadata)', { subscriptionStatus, subscriptionPlan });
      
      // Update local state and cache to match Clerk metadata (fix stale cache)
      const planFromMetadata = (subscriptionPlan || 'premium') as SubscriptionPlan;
      if (state.plan !== planFromMetadata || !state.isActive) {
        console.log('[useSubscription] Updating stale state/cache from Clerk metadata:', planFromMetadata);
        setState({
          plan: planFromMetadata,
          isActive: subscriptionStatus === 'active',
        });
        subscriptionService.cacheSubscription(planFromMetadata, undefined, false);
      }
      return false;
    }

    setIsAutoSubscribing(true);

    try {
      const token = await getToken();
      if (!token) {
        setIsAutoSubscribing(false);
        return false;
      }

      const apiBase = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiBase}/api/billing/auto-subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ discountCode }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[useSubscription] Auto-subscribe successful:', result);
        
        // Clear old cache and immediately cache the new subscription data
        subscriptionService.clearCache();
        
        const newPlan = (result.plan || 'premium') as SubscriptionPlan;
        const isActive = result.status === 'active';
        
        // Cache the new subscription immediately so it persists across page loads
        subscriptionService.cacheSubscription(newPlan, undefined, false);
        console.log('[useSubscription] Cached new subscription:', { plan: newPlan, isActive });
        
        // Set state directly from response (don't wait for Clerk sync)
        setState({
          plan: newPlan,
          isActive: isActive,
        });
        
        // Also reload Clerk user data in background for future consistency
        // Small delay to ensure Clerk metadata is synced
        setTimeout(async () => {
          if (user?.reload) {
            console.log('[useSubscription] Reloading Clerk user data in background...');
            await user.reload();
            await fetchSubscription();
          }
        }, 1000);
        
        setIsAutoSubscribing(false);
        return true;
      } else if (response.status === 409) {
        // User already has subscription - refresh to get current data
        console.log('[useSubscription] User already has subscription, refreshing...');
        subscriptionService.clearCache();
        if (user?.reload) {
          await user.reload();
        }
        await fetchSubscription();
        setIsAutoSubscribing(false);
        return true; // Return true since user has access
      } else {
        // Coupon not valid or not 100% - user needs to go through normal checkout
        setIsAutoSubscribing(false);
        return false;
      }
    } catch (error) {
      console.error('[useSubscription] Error auto-subscribing:', error);
      setIsAutoSubscribing(false);
      return false;
    }
  }, [discountCode, isLoaded, user, state.plan, state.isActive, getToken, fetchSubscription]);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, [fetchSubscription, fetchPlans]);

  // Auto-subscribe is NOT called automatically on page load
  // It will be called when user tries to access a premium feature via checkFeatureAccess

  const checkFeatureAccess = useCallback(
    (feature: 'replay' | 'tickReplay' | 'multiChart' | 'advancedAnalysis' | 'api' | 'aiAnalysis'): boolean => {
      const hasAccess = subscriptionService.hasFeatureAccess(state, feature);
      
      // If user doesn't have access but has a discount code, trigger auto-subscribe
      // This happens when user tries to access a premium feature
      if (!hasAccess && discountCode && !autoSubscribeAttempted.current) {
        // Trigger auto-subscribe in background (don't await)
        autoSubscribe();
      }
      
      return hasAccess;
    },
    [state, discountCode, autoSubscribe]
  );

  const refreshSubscription = useCallback(async () => {
    subscriptionService.clearCache();
    // Force Clerk to reload user data from server
    if (user?.reload) {
      await user.reload();
    }
    await fetchSubscription();
  }, [fetchSubscription, user]);

  const openUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
  }, []);

  const requiredPlanForFeature = useCallback((feature: string): SubscriptionPlan => {
    const requirements: Record<string, SubscriptionPlan> = {
      replay: 'essential',
      tickReplay: 'plus',
      multiChart: 'essential',
      advancedAnalysis: 'plus',
      api: 'premium',
      aiAnalysis: 'premium',
    };
    return requirements[feature] || 'essential';
  }, []);

  return {
    ...state,
    isLoading,
    availablePlans,
    isPlansLoading,
    checkFeatureAccess,
    refreshSubscription,
    openUpgradeModal,
    closeUpgradeModal,
    isUpgradeModalOpen,
    requiredPlanForFeature,
    isAutoSubscribing,
  };
}
